// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { FlexProps, ModalProps } from '@iheartjane/reefer';
import {
  Flex,
  // eslint-disable-next-line no-restricted-imports
  Modal as ReeferModal,
  useVisualViewportHeight,
} from '@iheartjane/reefer';
import { useMemo } from 'react';

import { useIpadMediaQuery } from '../../hooks';
import type { IpadOrientation } from '../../hooks';

interface Props extends Exclude<ModalProps, 'parentSelector'> {
  containerProps?: FlexProps;
  selector?: string;
}

const defaultModalVariant = 'standard-dialogue';
const heightRestrictedVariants = ['dialogue', 'standard-dialogue'];

const getModalHeight = (
  orientation: IpadOrientation,
  /**
   * Height of the visible viewport, in pixels.
   * An integer is preferable to '100vh' because of visual space taken up by on-screen keyboards.
   */
  viewportHeight: number
) => {
  let verticalPadding = 0;

  switch (orientation) {
    case 'landscape':
      verticalPadding = 128;
      break;
    case 'portrait':
      verticalPadding = 96;
      break;
    default:
      verticalPadding = 128;
  }

  const maxModalHeight = 1160;

  return `min(calc(${viewportHeight}px - ${verticalPadding}px), ${maxModalHeight}px)`;
};

const ModalContainerDialog = styled(Flex)(() => {
  const { viewportHeight } = useVisualViewportHeight();
  const ipadOrientation = useIpadMediaQuery();

  return {
    maxHeight: getModalHeight(ipadOrientation, viewportHeight),
  };
});

const ModalContainerStandardDialog = styled(Flex)(() => {
  const { viewportHeight } = useVisualViewportHeight();
  const ipadOrientation = useIpadMediaQuery();

  const modalHeight = getModalHeight(ipadOrientation, viewportHeight);

  return {
    maxHeight: modalHeight,
    minHeight: modalHeight,
  };
});

/**
 * When the modal is open, prevent the body from scrolling and to maintain scroll position.
 * Additional styles especially target iOS Safari.
 * @see https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/#aa-lets-enhance-the-fixed-body-approach
 */
const onAfterOpen = () => {
  document.body.style.overflow = 'hidden';
  document.body.style.top = `-${window.scrollY}px`;
};
const onAfterClose = () => {
  const scrollY = document.body.style.top;
  document.body.style.overflow = 'unset';
  document.body.style.top = '';
  window.scrollTo(0, parseInt(scrollY || '0') * -1);
};

/**
 * @param {string} [selector] - html selector for the parent element.
 * @param {ModalProps} props all modal props can be passed in except `parentSelector`.
 * @returns Reefer Modal component with selected parent.
 *
 * In order to have some modals show over others, namely the `IdlePrompt`,
 * all other modals in the Kiosk app should use this component. This hosts
 * most modals in the div with id `root` in `apps/kiosk/src/index.html`, while
 * others fall back to the default behavior of being placed in `body`.
 */
const KioskModal = ({
  children,
  containerProps = { flexDirection: 'column' },
  selector = '#modal-root',
  variant = defaultModalVariant,
  ...props
}: Props) => {
  const parentSelect = useMemo(() => {
    const parent = document.querySelector<HTMLElement>(selector);
    return parent ? () => parent : undefined;
  }, [selector]);

  return (
    <ReeferModal
      {...props}
      centerByVisualViewport
      onAfterClose={onAfterClose}
      onAfterOpen={onAfterOpen}
      parentSelector={parentSelect}
      variant={variant}
    >
      {variant === 'dialogue' && (
        <ModalContainerDialog {...containerProps}>
          {children}
        </ModalContainerDialog>
      )}
      {variant === 'standard-dialogue' && (
        <ModalContainerStandardDialog {...containerProps}>
          {children}
        </ModalContainerStandardDialog>
      )}
      {!heightRestrictedVariants.includes(variant) && children}
    </ReeferModal>
  );
};

export const Modal = Object.assign(KioskModal, {
  Header: ReeferModal.Header,
  Content: ReeferModal.Content,
  ContentDivider: ReeferModal.ContentDivider,
  Footer: ReeferModal.Footer,
});
